import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { 
  TrendingUp, 
  Eye, 
  MousePointer, 
  DollarSign, 
  BarChart3,
  Download,
  RefreshCw,
  Calendar,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [dateRange, setDateRange] = useState('30');
  const [overallMetrics, setOverallMetrics] = useState({
    total_impressions: 0,
    total_clicks: 0,
    total_revenue: 0,
    average_ctr: 0
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (campaigns.length > 0) {
      calculateMetrics();
    }
  }, [campaigns, selectedCampaign]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/admin/campaigns`);
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast({
        title: 'Error',
        description: 'Failed to load campaign data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    let filteredCampaigns = campaigns;
    
    if (selectedCampaign !== 'all') {
      filteredCampaigns = campaigns.filter(c => c.id === selectedCampaign);
    }

    const totals = filteredCampaigns.reduce((acc, campaign) => {
      acc.total_impressions += campaign.impressions || 0;
      acc.total_clicks += campaign.clicks || 0;
      acc.total_revenue += campaign.revenue || 0;
      return acc;
    }, {
      total_impressions: 0,
      total_clicks: 0,
      total_revenue: 0
    });

    const ctr = totals.total_impressions > 0 
      ? ((totals.total_clicks / totals.total_impressions) * 100).toFixed(2)
      : 0;

    setOverallMetrics({
      ...totals,
      average_ctr: parseFloat(ctr)
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-ZA').format(num || 0);
  };

  const getCTRColor = (ctr) => {
    if (ctr >= 2) return 'text-green-600';
    if (ctr >= 1) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceRating = (ctr) => {
    if (ctr >= 2) return 'Excellent';
    if (ctr >= 1) return 'Good';
    if (ctr >= 0.5) return 'Average';
    return 'Needs Improvement';
  };

  const exportData = () => {
    // Create CSV data
    const headers = ['Campaign', 'Brand', 'Status', 'Impressions', 'Clicks', 'CTR', 'Revenue', 'Budget'];
    const rows = campaigns.map(c => [
      c.campaign_name,
      c.brand_name,
      c.status,
      c.impressions,
      c.clicks,
      ((c.clicks / c.impressions * 100) || 0).toFixed(2) + '%',
      c.revenue,
      c.budget
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chirosport-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Success',
      description: 'Analytics data exported successfully'
    });
  };

  const MetricCard = ({ title, value, icon: Icon, color, trend, subtitle }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
            <p className={`text-3xl font-bold ${color}`}>{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-full bg-opacity-10 ${color.replace('text-', 'bg-')}`}>
            <Icon className={color} size={28} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const CampaignPerformanceTable = () => {
    const displayCampaigns = selectedCampaign === 'all' 
      ? campaigns 
      : campaigns.filter(c => c.id === selectedCampaign);

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left p-3 font-semibold text-gray-700">Campaign</th>
              <th className="text-left p-3 font-semibold text-gray-700">Brand</th>
              <th className="text-center p-3 font-semibold text-gray-700">Status</th>
              <th className="text-right p-3 font-semibold text-gray-700">Impressions</th>
              <th className="text-right p-3 font-semibold text-gray-700">Clicks</th>
              <th className="text-right p-3 font-semibold text-gray-700">CTR</th>
              <th className="text-right p-3 font-semibold text-gray-700">Revenue</th>
              <th className="text-right p-3 font-semibold text-gray-700">Budget</th>
              <th className="text-center p-3 font-semibold text-gray-700">Performance</th>
            </tr>
          </thead>
          <tbody>
            {displayCampaigns.map((campaign) => {
              const ctr = campaign.impressions > 0 
                ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2)
                : 0;
              const budgetUsed = (campaign.revenue / campaign.budget * 100).toFixed(1);

              return (
                <tr key={campaign.id} className="border-b hover:bg-gray-50">
                  <td className="p-3">
                    <div>
                      <p className="font-medium">{campaign.campaign_name}</p>
                      <p className="text-xs text-gray-500">{campaign.pricing_model}</p>
                    </div>
                  </td>
                  <td className="p-3 text-gray-600">{campaign.brand_name}</td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                      campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {campaign.status}
                    </span>
                  </td>
                  <td className="p-3 text-right font-mono">{formatNumber(campaign.impressions)}</td>
                  <td className="p-3 text-right font-mono">{formatNumber(campaign.clicks)}</td>
                  <td className={`p-3 text-right font-mono font-bold ${getCTRColor(parseFloat(ctr))}`}>
                    {ctr}%
                  </td>
                  <td className="p-3 text-right font-mono">{formatCurrency(campaign.revenue)}</td>
                  <td className="p-3 text-right">
                    <div>
                      <p className="font-mono">{formatCurrency(campaign.budget)}</p>
                      <p className="text-xs text-gray-500">{budgetUsed}% used</p>
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`text-xs font-semibold ${getCTRColor(parseFloat(ctr))}`}>
                      {getPerformanceRating(parseFloat(ctr))}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-[#2c3e7d] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Admin Dashboard
        </button>
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Campaign performance and revenue tracking</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchCampaigns}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button onClick={exportData}>
            <Download size={16} className="mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex gap-2 items-center">
              <BarChart3 size={20} className="text-gray-600" />
              <Label>Campaign:</Label>
              <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.campaign_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 items-center">
              <Calendar size={20} className="text-gray-600" />
              <Label>Period:</Label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overall Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Impressions"
          value={formatNumber(overallMetrics.total_impressions)}
          icon={Eye}
          color="text-blue-600"
          subtitle="Ad views"
        />
        <MetricCard
          title="Total Clicks"
          value={formatNumber(overallMetrics.total_clicks)}
          icon={MousePointer}
          color="text-green-600"
          subtitle="User engagement"
        />
        <MetricCard
          title="Average CTR"
          value={`${overallMetrics.average_ctr}%`}
          icon={TrendingUp}
          color={getCTRColor(overallMetrics.average_ctr)}
          subtitle={getPerformanceRating(overallMetrics.average_ctr)}
        />
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(overallMetrics.total_revenue)}
          icon={DollarSign}
          color="text-purple-600"
          subtitle="From all campaigns"
        />
      </div>

      {/* Campaign Performance Table */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Campaign Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <div className="text-center py-12">
              <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600">No campaigns found</p>
              <p className="text-gray-500 text-sm mt-2">Create your first campaign to see analytics</p>
            </div>
          ) : (
            <CampaignPerformanceTable />
          )}
        </CardContent>
      </Card>

      {/* Performance Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">📊 CTR Benchmarks</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Excellent:</strong> 2%+ (Your ads are highly engaging)</li>
                <li>• <strong>Good:</strong> 1-2% (Above average performance)</li>
                <li>• <strong>Average:</strong> 0.5-1% (Room for improvement)</li>
                <li>• <strong>Needs Improvement:</strong> Below 0.5% (Review creative and targeting)</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">💡 Optimization Tips</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Test multiple creative variants for each campaign</li>
                <li>• Place ads in high-visibility locations (above the fold)</li>
                <li>• Refresh creative assets every 2-3 weeks to combat ad fatigue</li>
                <li>• Target contextually relevant placements</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

// Missing Label component
const Label = ({ children, className = '' }) => (
  <label className={`text-sm font-medium text-gray-700 ${className}`}>
    {children}
  </label>
);

export default AnalyticsDashboard;
